import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const EditProductModal = ({ 
  isOpen, 
  onClose, 
  product, 
  categories, 
  onSave, 
  onDelete,
  
}) => {
  const { t } = useTranslation("bulkproducts");
  const [formData, setFormData] = useState({
    sku: "",
    name_en: "",
    name_ar: "",
    description_en: "",
    description_ar: "",
    price: "",
    stock: "",
    min_order: "",
    category_id: "",
    shipping_period: "",
    images: ""
  });
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditDeleteConfirm, setShowEditDeleteConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data when product changes
  useEffect(() => {
    if (product && isOpen) {
      const rawData = product.rawData || {};
      setFormData({
        sku: rawData.sku || product.sku || "",
        name_en: rawData.name_en || product.productName || "",
        name_ar: rawData.name_ar || "",
        shipping_period: rawData.shipping_period || "",
        description_en: rawData.description_en || product.description_en || "",
        description_ar: rawData.description_ar || "",
        price: rawData.price || product.price || "",
        stock: rawData.stock || product.stock || "",
        min_order: rawData.min_order || product.min_order || "",
        category_id: rawData.category_id || "",
        images: rawData.images || product.photo || ""
      });

      // Parse image URLs
      const imgStr = rawData.images || product.photo || "";
      if (imgStr) {
        const urls = imgStr
        .split(/,(?=https?:\/\/)/) // split only when a new URL starts
        .map(url => url.trim()).filter(Boolean);
        setImageUrls(urls);
      } else {
        setImageUrls([]);
      }
    }
    // clear errors when modal opens
    if (isOpen) {
      setErrors({});
    }
    // close confirm dialog when modal closes
    if (!isOpen) {
      setShowEditDeleteConfirm(false);
    }
  }, [product, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleImageUrlChange = (index, value) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
    
    // Update images string in form data
    const imagesString = newUrls.filter(Boolean).join(",");
    setFormData(prev => ({
      ...prev,
      images: imagesString
    }));

    // Clear image error when user adds/modifies images
    if (errors.images) {
      setErrors(prev => ({
        ...prev,
        images: ""
      }));
    }
  };

  const addImageUrl = () => {
    setImageUrls([...imageUrls, ""]);
  };

  const removeImageUrl = (index) => {
    const newUrls = imageUrls.filter((_, i) => i !== index);
    setImageUrls(newUrls);
    
    const imagesString = newUrls.filter(Boolean).join(",");
    setFormData(prev => ({
      ...prev,
      images: imagesString
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validations
    if (!formData.name_en.trim()) {
      newErrors.name = t("validation.nameRequired");
    }
    
    if (!formData.description_en.trim()) {
      newErrors.description = t("validation.descriptionRequired");
    }
    
    if (!formData.price || formData.price.toString().trim() === "") {
      newErrors.price = t("validation.priceRequired");
    } else if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) {
      newErrors.price = t("validation.priceInvalid");
    }
    
    // Integer validations
    if (!formData.stock || formData.stock.toString().trim() === "") {
      newErrors.stock = t("validation.stockRequired");
    } else if (!Number.isInteger(Number(formData.stock)) || parseInt(formData.stock) < 0) {
      newErrors.stock = t("validation.stockInvalid");
    }
    
    if (!formData.min_order || formData.min_order.toString().trim() === "") {
      newErrors.min_order = t("validation.minOrderRequired");
    } else if (!Number.isInteger(Number(formData.min_order)) || parseInt(formData.min_order) < 1) {
      newErrors.min_order = t("validation.minOrderInvalid");
    }
    
    if (!formData.shipping_period || formData.shipping_period.toString().trim() === "") {
      newErrors.shipping_period = t("validation.shippingPeriodRequired");
    } else if (!Number.isInteger(Number(formData.shipping_period)) || parseInt(formData.shipping_period) < 1) {
      newErrors.shipping_period = t("validation.shippingPeriodInvalid");
    }
    
    if (!formData.category_id || formData.category_id.toString().trim() === "") {
      newErrors.category_id = t("validation.categoryRequired");
    }
    
    // Image validation - at least one valid image URL required
    const validImageUrls = imageUrls.filter(url => url && url.trim() !== "");
    if (validImageUrls.length === 0) {
      newErrors.images = t("validation.imageRequired");
    } else {
      // Validate image URLs format
      const invalidUrls = validImageUrls.filter(url => {
        try {
          return !url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/');
        } catch {
          return true;
        }
      });
      if (invalidUrls.length > 0) {
        newErrors.images = t("validation.imageUrlInvalid");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      await onSave(product.id, formData);
      onClose();
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    console.log("Deleting product in modal:", product);
    setDeleting(true);
    try {
      await onDelete(product);
      setShowEditDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error("Error deleting product:", error);
    } finally {
      setDeleting(false);
    }
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId) return "";
    const category = categories.find(c => c.id === parseInt(categoryId));
    return category ? category.name : "";
  };
// close modal when click outside
  // const handleClickOutside = (event) => {
  //   const modal = document.getElementById("edit-product-modal");
  //   // except if clicking inside the modal or on the confirm delete dialog
  //   if( showEditDeleteConfirm ) {
  //     return;
  //   }
  //   if (modal && !modal.contains(event.target)) {
  //     // clear errors
  //     setErrors({});
  //     onClose();
  //   }
  // };
  //   useEffect(() => {
  //   if (isOpen) {
  //       document.addEventListener("mousedown", handleClickOutside);
  //   } else {
  //       document.removeEventListener("mousedown", handleClickOutside);
  //   }
  //   return () => {
  //       document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div id="edit-product-modal" className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[75vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {t("edit.title")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* SKU */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("edit.sku")}
            </label>
            <input
              type="text"
              value={formData.sku}
              onChange={(e) => handleInputChange("sku", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={t("edit.skuPlaceholder")}
            />
          </div>

          {/* Product Names */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("edit.name")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder={t("edit.namePlaceholder")}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("edit.nameAr")}
              </label>
              <input
                type="text"
                value={formData.name_ar}
                onChange={(e) => handleInputChange("name_ar", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t("edit.nameArPlaceholder")}
                dir="rtl"
              />
            </div>
          </div>

          {/* Descriptions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("edit.description")} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none ${
                  errors.description ? "border-red-500" : "border-gray-300"
                }`}
                placeholder={t("edit.descriptionPlaceholder")}
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("edit.descriptionAr")}
              </label>
              <textarea
                value={formData.description_ar}
                onChange={(e) => handleInputChange("description_ar", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder={t("edit.descriptionArPlaceholder")}
                dir="rtl"
              />
            </div>
          </div>

          {/* Price, Stock, Min Order, Shipping_period */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("edit.price")} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => handleInputChange("price", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.price ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="0.00"
              />
              {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("edit.stock")} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.stock}
                onChange={(e) => handleInputChange("stock", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.stock ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="0"
              />
              {errors.stock && <p className="text-red-500 text-sm mt-1">{errors.stock}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("edit.minOrder")} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={formData.min_order}
                onChange={(e) => handleInputChange("min_order", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.min_order ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="1"
              />
              {errors.min_order && <p className="text-red-500 text-sm mt-1">{errors.min_order}</p>}
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("edit.shippingPeriod")} <span className="text-red-500">*</span>
                </label>
                <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.shipping_period}
                    onChange={(e) => handleInputChange("shipping_period", e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        errors.shipping_period ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="1"
                />
                {errors.shipping_period && <p className="text-red-500 text-sm mt-1">{errors.shipping_period}</p>}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("edit.category")} <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.category_id}
              onChange={(e) => handleInputChange("category_id", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.category_id ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">{t("edit.selectCategory")}</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {errors.category_id && <p className="text-red-500 text-sm mt-1">{errors.category_id}</p>}
            {formData.category_id && (
              <p className="text-sm text-gray-500 mt-1">
                {t("edit.selectedCategory")}: {getCategoryName(formData.category_id)}
              </p>
            )}
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("edit.images")} <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {imageUrls.map((url, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => handleImageUrlChange(index, e.target.value)}
                    className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      errors.images ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder={t("edit.imageUrlPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => removeImageUrl(index)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addImageUrl}
                className="flex items-center gap-2 px-3 py-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t("edit.addImage")}
              </button>
            </div>
            {errors.images && <p className="text-red-500 text-sm mt-1">{errors.images}</p>}
            {formData.images && imageUrls.filter(Boolean).length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-2">{t("edit.imagePreview")}:</p>
                <div className="flex flex-wrap gap-2">
                  {imageUrls.filter(Boolean).map((url, index) => (
                    <div key={index} className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                      <img
                        key={url}
                        src={url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                        onLoad={(e) => {
                          e.target.style.display = 'block';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'none';
                          }
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = "flex";
                          }
                        }}
                      />
                      <div
                        className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
                        style={{ display: "none" }}
                      >
                        ERR
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={() => setShowEditDeleteConfirm(true)}
            className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {t("edit.delete")}
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t("edit.cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                loading
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#6609F8] text-white hover:bg-purple-700"
              }`}
            >
              {loading && (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? t("edit.saving") : t("edit.save")}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showEditDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("edit.confirmDelete")}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t("edit.deleteWarning")}
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-700">
                  {formData.name || product?.productName}
                </p>
                {formData.sku && (
                  <p className="text-xs text-gray-500">SKU: {formData.sku}</p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    
                    setShowEditDeleteConfirm(false);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t("edit.cancelDelete")}
                </button>
                <button
                  onClick={
                    () => {
                      console.log("Delete confirmed for product:", product);
                      handleDelete();
                    }
                  }
                  disabled={deleting}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    deleting
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                >
                  {deleting && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {deleting ? t("edit.deleting") : t("edit.confirmDeleteBtn")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProductModal;